import { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Download, X, ZoomIn, ZoomOut, Maximize2, Minimize2, Loader2, FileWarning, ChevronLeft, ChevronRight, PanelRight, RotateCw } from 'lucide-react';

// pdfjs worker — served from /public so it matches the installed lib exactly
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const IMG = /\.(png|jpe?g|webp|gif)$/i;
const PDF = /\.pdf$/i;

// In-portal document viewer: PDFs render via pdf.js (canvas — works on desktop AND
// mobile, never downloads), images render inline with zoom. Anything else → download.
export function DocPreviewModal({ file, files = [], onNavigate, onClose, fetchBlob, onDownload, side = null }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [showSide, setShowSide] = useState(true);
  const [err, setErr] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [full, setFull] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const pdfRef = useRef(null);
  const bodyRef = useRef(null);
  const name = file?.original_filename || 'Document';
  const isImg = IMG.test(name) || (file?.content_type || '').startsWith('image/');
  const isPdf = PDF.test(name) || file?.content_type === 'application/pdf';

  // Load the file: images → object URL; PDFs → pdf.js document
  useEffect(() => {
    let alive = true; let obj = null;
    setImgUrl(null); setErr(null); setZoom(1); setRot(0); setNumPages(0); pdfRef.current = null;
    if (!file || (!isImg && !isPdf)) { setLoading(false); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const blob = await fetchBlob(file);
        if (!alive) return;
        if (isPdf) {
          const buf = await blob.arrayBuffer();
          if (!alive) return;
          const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
          if (!alive) { pdf.destroy?.(); return; }
          pdfRef.current = pdf;
          setNumPages(pdf.numPages);
        } else {
          obj = URL.createObjectURL(blob);
          setImgUrl(obj);
        }
      } catch (e) {
        if (alive) setErr(e?.response?.status === 404 ? 'File not found' : 'Could not load this file');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); const p = pdfRef.current; if (p) { try { p.destroy(); } catch (_) { /* noop */ } } };
  }, [file, fetchBlob, isImg, isPdf]);

  // Render each PDF page onto its canvas (re-runs on zoom / fullscreen / page count)
  const renderPdf = useCallback(async () => {
    const pdf = pdfRef.current;
    if (!pdf || !bodyRef.current) return;
    const containerW = bodyRef.current.clientWidth || 800;
    const targetW = Math.min(Math.max(containerW - 32, 280), 1100) * zoom;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (let n = 1; n <= pdf.numPages; n += 1) {
      const canvas = document.getElementById(`docpdf-page-${n}`);
      if (!canvas) continue;
      // eslint-disable-next-line no-await-in-loop
      const page = await pdf.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const scale = targetW / base.width;
      const viewport = page.getViewport({ scale: scale * dpr });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      const ctx = canvas.getContext('2d');
      // eslint-disable-next-line no-await-in-loop
      await page.render({ canvasContext: ctx, viewport }).promise;
    }
  }, [zoom]);

  useEffect(() => { if (numPages > 0) { renderPdf(); } }, [numPages, renderPdf, full]);

  useEffect(() => { const k = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  if (!file) return null;
  const idx = files.findIndex((f) => f.docId === file.docId && f.idx === file.idx);
  const canZoom = isImg || isPdf;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0B0A1F]/80 backdrop-blur-sm p-2 sm:p-6" data-testid="doc-preview-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className={`flex flex-col bg-white shadow-2xl overflow-hidden ${full ? 'fixed inset-0 rounded-none' : `w-full ${side ? 'max-w-7xl' : 'max-w-5xl'} h-[92vh] sm:h-[88vh] rounded-2xl`}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 sm:px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-900" data-testid="doc-preview-title">{file.docName || name}</p>
            <p className="truncate text-[11px] text-slate-500">{name}{file.applicantLabel ? ` · ${file.applicantLabel}` : ''}{file.size ? ` · ${Math.round(file.size / 1024)} KB` : ''}</p>
          </div>
          {side && <button type="button" onClick={() => setShowSide((s) => !s)} data-testid="doc-preview-toggle-context" className={`hidden lg:inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold transition-colors ${showSide ? 'bg-[#15133A]/5 text-[#15133A]' : 'text-slate-600 hover:bg-slate-100'}`}><PanelRight className="w-3.5 h-3.5" /> Review context</button>}
          {files.length > 1 && <span className="hidden sm:flex items-center gap-1" data-testid="doc-preview-nav"><button type="button" disabled={idx <= 0} onClick={() => onNavigate(files[idx - 1])} data-testid="doc-preview-prev" className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button><span className="text-[11px] text-slate-500 tabular-nums">{idx + 1}/{files.length}</span><button type="button" disabled={idx >= files.length - 1} onClick={() => onNavigate(files[idx + 1])} data-testid="doc-preview-next" className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button></span>}
          {canZoom && <><button type="button" onClick={() => setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)))} data-testid="doc-preview-zoom-out" className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100"><ZoomOut className="w-4 h-4" /></button><span className="text-[11px] text-slate-500 w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span><button type="button" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} data-testid="doc-preview-zoom-in" className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100"><ZoomIn className="w-4 h-4" /></button><button type="button" onClick={() => setRot((r) => (r + 90) % 360)} data-testid="doc-preview-rotate" title="Rotate" className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100"><RotateCw className="w-4 h-4" /></button></>}
          <button type="button" onClick={() => setFull((f) => !f)} data-testid="doc-preview-fullscreen" className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100" title={full ? 'Exit full screen' : 'Full screen'}>{full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}</button>
          <button type="button" onClick={() => onDownload(file)} data-testid="doc-preview-download" className="inline-flex items-center gap-1.5 rounded-lg bg-[#15133A] px-3 py-1.5 text-xs font-bold text-white"><Download className="w-3.5 h-3.5" /> Download</button>
          <button type="button" onClick={onClose} data-testid="doc-preview-close" className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex flex-1 min-h-0">
        <div ref={bodyRef} className="relative flex-1 overflow-auto bg-slate-100" data-testid="doc-preview-body">
          {!isImg && !isPdf && (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center" data-testid="doc-preview-unsupported">
              <FileWarning className="w-10 h-10 text-amber-500" />
              <p className="text-sm font-bold text-slate-900">This file type can&apos;t be previewed here</p>
              <p className="text-xs text-slate-500 max-w-sm">{name} — download it to open with the right application on your device.</p>
              <button type="button" onClick={() => onDownload(file)} className="inline-flex items-center gap-1.5 rounded-xl bg-[#15133A] px-4 py-2.5 text-sm font-bold text-white"><Download className="w-4 h-4" /> Download file</button>
            </div>
          )}
          {canZoom && loading && <div className="flex h-full items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
          {err && <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-rose-700"><FileWarning className="w-8 h-8" />{err}</div>}
          {isPdf && !err && numPages > 0 && (
            <div className="flex flex-col items-center gap-4 py-4 transition-transform duration-200 origin-top" style={{ transform: `rotate(${rot}deg)` }} data-testid="doc-preview-pdf">
              {Array.from({ length: numPages }, (_, i) => (
                <canvas key={i + 1} id={`docpdf-page-${i + 1}`} className="bg-white shadow-lg max-w-full" data-testid={`doc-preview-pdf-page-${i + 1}`} />
              ))}
            </div>
          )}
          {isImg && imgUrl && !err && <div className="flex min-h-full items-start justify-center p-4"><img src={imgUrl} alt={name} data-testid="doc-preview-image" style={{ transform: `scale(${zoom}) rotate(${rot}deg)`, transformOrigin: 'top center' }} className="max-w-full shadow-lg transition-transform duration-150" /></div>}
        </div>
        {side && showSide && <div className="hidden lg:block w-[320px] shrink-0 border-l border-slate-200 overflow-hidden" data-testid="doc-preview-side">{side}</div>}
        </div>
      </div>
    </div>
  );
}
